/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

// eslint-disable-next-line import/no-extraneous-dependencies
import { formatDate } from '@squiz/dxp-console-support';
import { MetricClass } from '@squiz/dxp-metric-collection-types';
import {
  BUTTON_FORMAT_STRONG,
  BUTTON_THEME_PRIMARY,
  Button,
  EmptyState,
  ICON_INFO_WITH_LIST,
  LineChart,
  LineChartProps,
  SPINNER_TYPE_REGULAR,
  Spinner,
} from '@squiz/sds';
import {
  MetricCollectionServiceGetMetricSeriesDto,
  PeriodSize,
  getMetricCollectionService,
} from 'apps/console-core/src/Services/metric-collection';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import isUndefined from 'lodash/isUndefined';
import property from 'lodash/property';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { lastValueFrom } from 'rxjs';
import { CDP_STRINGS } from '../../../../../../Constants/Strings.constants';
import { EntitySegmentEventSubscription, useEvents } from '../../../../../../Services/segmentation';
import { emptyMetricData } from './SuccessMetricOutcome.mocks';
import { SuccessMetricOutcomeProps } from './SuccessMetricOutcome.props';
import { SuccessMetricData, SuccessMetricDataResult, SuccessMetricOutcomeChart } from './SuccessMetricOutcome.types';

/**
 * Format metric data into chart props.
 * @param {SuccessMetricDataResult} metricData The metric data.
 * @returns {LineChartProps} The chart props.
 */
export const formatSuccessMetricDataAsChartProps = (metricData: SuccessMetricDataResult): LineChartProps => {
  const months = [`Jan`, `Feb`, `Mar`, `Apr`, `May`, `Jun`, `Jul`, `Aug`, `Sep`, `Oct`, `Nov`, `Dec`];
  const valueMap: Record<string, number> = {};
  let data = [...metricData.data];
  data = data.sort((a, b): number => {
    return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
  });
  data.forEach((data) => {
    const t = new Date(data.timestamp);
    const key = `${months[t.getMonth()]} ${t.getDate()}`;
    if (property(key)(valueMap)) {
      valueMap[key] += data.value;
    } else {
      valueMap[key] = data.value;
    }
  });
  return {
    axisXLabel: 'Day',
    axisYLabel: 'Occurrences',
    height: 200,
    hideLegend: true,
    labels: Object.keys(valueMap),
    plottingOptions: [{ data: Object.values(valueMap) }],
  };
};

/**
 * Merge a source metric data onto target metric data.
 * Not:; Generally this should be a source that maps out 0 values for each day.
 * @param {SuccessMetricDataResult['data']} source Source metric data.
 * @param {SuccessMetricDataResult['data']} target Target metric data.
 * @returns {SuccessMetricDataResult['data']} Combined metric data.
 */
export const mergeMetricData = (
  source: SuccessMetricDataResult['data'],
  target: SuccessMetricDataResult['data'],
): SuccessMetricDataResult['data'] => {
  return target.map((targetItem) => {
    const t = { ...targetItem };
    source.forEach((sourceItem) => {
      if (sourceItem.timestamp === targetItem.timestamp) {
        t.value = sourceItem.value;
      }
    });
    return t;
  });
};

export const getLastUpdatedFromMetricData = (metricData: SuccessMetricDataResult): string => {
  let data = [...metricData.data];
  data = data.sort((a, b): number => {
    return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
  });
  return formatDate((data.shift() as SuccessMetricData).timestamp, true);
};

/**
 * View customer success metric outcome information from a chosen segment.
 * @param {SuccessMetricOutcomeProps} props The success metric outcome props.
 * @export
 * @returns {JSX.Element} The `<CustomerView />` component.
 */
export const SuccessMetricOutcome = (props: SuccessMetricOutcomeProps): JSX.Element => {
  const { activeSegment } = props;
  const eventList = useEvents(activeSegment?.internalId);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const metricEntries = useMemo(() => {
    const metrics = property(`success`)(activeSegment) || {};
    return Object.entries(metrics);
  }, [activeSegment]);

  const [charts, setCharts] = useState<Array<SuccessMetricOutcomeChart>>([]);
  const [isLoadingCharts, setLoadingCharts] = useState(metricEntries.length !== 0);

  const labels = useMemo(() => {
    return {
      addSuccessMetric: t(CDP_STRINGS.addSuccessMetric),
      defaultLastUpdated: t(CDP_STRINGS.noSuccessMetrics),
      successMetricEmptyStateDescription: t(CDP_STRINGS.successMetricEmptyStateDescription),
    };
  }, [t]);

  useEffect(() => {
    if (!eventList) {
      return;
    }

    Promise.all(
      metricEntries.map(async ([eventId, metric]): Promise<SuccessMetricOutcomeChart | undefined> => {
        const event = (property(eventId)(eventList) as EntitySegmentEventSubscription) || null;
        let chart: SuccessMetricOutcomeChart | undefined;
        if (!event) {
          throw new Error(`Missing chart data`);
        }

        if (metric.metricid) {
          const metricCollectionService = getMetricCollectionService();
          const now = new Date();
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(now.getDate() - 7);
          const metricSeriesDto: MetricCollectionServiceGetMetricSeriesDto = {
            dateFrom: sevenDaysAgo.toISOString(),
            dateTo: now.toISOString(),
            metricClass: MetricClass.custom,
            metricId: metric.metricid,
            periodSize: PeriodSize.day,
          };
          const metricData = metricCollectionService.getMetricSeries(metricSeriesDto);
          await lastValueFrom(metricData)
            .then((result) => {
              if (result === null) {
                setLoadingCharts(false);
                return;
              }
              result.data = mergeMetricData(result.data, emptyMetricData.data);
              chart = {
                chartProps: formatSuccessMetricDataAsChartProps(result),
                eventId,
                lastUpdated: result.data.length >= 1 ? getLastUpdatedFromMetricData(result) : labels.defaultLastUpdated,
                title: event.name,
              };
              setLoadingCharts(false);
              return;
            })
            .catch((error) => {
              setLoadingCharts(false);
              if (error.status === 404) {
                chart = {
                  chartProps: formatSuccessMetricDataAsChartProps(emptyMetricData),
                  eventId,
                  lastUpdated: labels.defaultLastUpdated,
                  title: event.name,
                };
              } else {
                // eslint-disable-next-line no-console
                console.error(error);
              }
            });
        }
        return chart;
      }),
    )
      .then((nextCharts) => {
        // Set any known charts.
        return setCharts([
          ...charts,
          ...(nextCharts.filter((chart) => !isUndefined(chart)) as Array<SuccessMetricOutcomeChart>),
        ]);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error.message);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventList, metricEntries]);

  if (!activeSegment) {
    return <></>;
  }

  if (!eventList || isLoadingCharts) {
    return (
      <div className="loading">
        <Spinner type={SPINNER_TYPE_REGULAR} />
      </div>
    );
  }

  const noSuccessMetric = isUndefined(activeSegment.success) || Object.keys(activeSegment.success).length === 0;
  if (noSuccessMetric) {
    return (
      <EmptyState caption={labels.successMetricEmptyStateDescription} svg={ICON_INFO_WITH_LIST}>
        <Button
          data-id="add-success-metric-outcome"
          format={BUTTON_FORMAT_STRONG}
          onClick={(): void => {
            const screenPath = getCapabilityPath(`/segmentation`);
            navigate({ pathname: screenPath, search: `segment=${activeSegment.code}` });
          }}
          text={labels.addSuccessMetric}
          theme={BUTTON_THEME_PRIMARY}
        />
      </EmptyState>
    );
  }

  return (
    <div className="success-metric-outcome">
      {charts.length >= 1 &&
        charts.map((chart) => {
          return (
            <div className="success-metric-outcome__chart" key={`success-metric-${chart.eventId}`}>
              <h4 className="success-metric-outcome__chart-title">{chart.title}</h4>
              <div className="success-metric-outcome__chart-meta">Last updated: {chart.lastUpdated}</div>
              <hr className="success-metric-outcome__chart-divider" />
              <div className="success-metric-outcome__chart-data">
                <LineChart {...chart.chartProps} />
              </div>
            </div>
          );
        })}
    </div>
  );
};
